import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAddMoneyStore, WalletCurrency } from '@/store/useAddMoneyStore'
import { useRouteTrackerStore } from '@/store/useRouteTrackerStore'
import { useGetWalletDataByCryptoCode } from '@/hooks/useGetWalletDataByCryptoCode'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'

interface ResetAddMoneyOnRouteEntryProps {
  cryptoCode?: string
}

export const ResetAddMoneyOnRouteEntry = ({
  cryptoCode,
}: ResetAddMoneyOnRouteEntryProps) => {
  const location = useLocation()
  const previousRoute = useRouteTrackerStore((s) => s.previousRoute)
  const currentRoute = useRouteTrackerStore((s) => s.currentRoute)
  const displayCurrency = useAppPreferencesStore((s) => s.displayCurrency)

  const setSelectedCurrency = useAddMoneyStore(
    (state) => state.setSelectedCurrency,
  )
  const setCryptoAmount = useAddMoneyStore((state) => state.setCryptoAmount)
  const setFiatAmount = useAddMoneyStore((state) => state.setFiatAmount)
  const setIsFiatMode = useAddMoneyStore((state) => state.setIsFiatMode)

  // Check that we are on the main /add-money page (not nested pages)
  const isMainAddMoneyPage = currentRoute === '/add-money'

  // Check if we need to reset the store - similar to send-request
  // Reset on every entry to /add-money, EXCEPT when returning from:
  // - /add-money/confirm (to preserve data if redirect due to error)
  // - validations-by-feature (system validation)
  // - currency selector modal (currency switching within flow)
  const shouldResetStore =
    isMainAddMoneyPage &&
    previousRoute !== '/add-money/confirm' &&
    !previousRoute?.includes('validations-by-feature') &&
    !previousRoute?.includes('currency-selector')

  // Check if we came from currency-details or select-deposit-type
  const byCurrencyDetails = previousRoute?.includes('currency-details')

  // Get currency data by code from store/API
  const defaultCryptoWallet = useGetWalletDataByCryptoCode({
    cryptoCode: byCurrencyDetails ? cryptoCode || 'USDT' : 'USDT',
  })

  // Get currency from location.state (if provided)
  const currencyFromState =
    location.state?.currency || location.state?.selectedCurrency

  // Priority: currency from state > currency from store/API
  const currencyToUse = (currencyFromState ||
    defaultCryptoWallet) as WalletCurrency

  useEffect(() => {
    // Reset state only if we are NOT returning from the confirmation page
    const isFromConfirmPage = location.state?.fromConfirm

    if (shouldResetStore && !isFromConfirmPage) {
      // Set fiat mode
      setIsFiatMode(true)

      // Set selected currency (priority for currency from state)
      if (currencyToUse) {
        setSelectedCurrency(currencyToUse)
      }

      // Reset amounts using new methods
      setFiatAmount({
        amount: '0',
        code: displayCurrency?.isin || 'USD',
      })
      setCryptoAmount({
        amount: '0',
        code: currencyToUse?.code || 'USDT',
      })
    }
  }, [
    shouldResetStore,
    currencyToUse,
    displayCurrency,
    location.state,
    setSelectedCurrency,
    setCryptoAmount,
    setFiatAmount,
    setIsFiatMode,
  ])

  // Separate effect for setting currency from state when navigating within add-money flow
  // For example, when navigating from /add-money/select-deposit-type to /add-money
  useEffect(() => {
    if (currencyFromState) {
      setSelectedCurrency(currencyFromState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyFromState?.code]) // Track only currency code changes

  return null
}
